import type { TourDef } from './types';

export interface ResolvedStep {
  key: string;
  element: HTMLElement;
  padding?: number;
}

/** On screen: rendered, not display:none and not visibility:hidden (a phone's closed drawer is). */
export function isShown(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return el.getClientRects().length > 0;
}

interface ResolveOptions {
  find?: (selector: string) => Element | null;
  shown?: (el: Element) => boolean;
  log?: (message: string) => void;
}

/**
 * The steps of a tour that can be shown on this page, now. A step whose target is
 * missing or hidden (a module switched off, the quiet mode bubble when quiet mode
 * is off, the sidebar on a phone) is left out and logged -- a tour never breaks
 * because the page differs from the one it was written on. Two steps landing on
 * the same element keep the first.
 */
export function resolveTourSteps(tour: TourDef, opts: ResolveOptions = {}): ResolvedStep[] {
  const find = opts.find || ((s: string) => document.querySelector(s));
  const shown = opts.shown || isShown;
  // eslint-disable-next-line no-console
  const log = opts.log || ((m: string) => console.info(m));
  const used = new Set<Element>();
  const out: ResolvedStep[] = [];
  for (const step of tour.steps) {
    const selectors = Array.isArray(step.target) ? step.target : [step.target];
    let element: HTMLElement | null = null;
    for (const selector of selectors) {
      let candidate: Element | null = null;
      try { candidate = find(selector); } catch { candidate = null; }
      if (candidate && shown(candidate)) { element = candidate as HTMLElement; break; }
    }
    if (!element) {
      log(`[help] tour "${tour.id}": step "${step.key}" skipped, nothing on the page matches ${selectors.join(' or ')}`);
      continue;
    }
    if (used.has(element)) continue;
    used.add(element);
    out.push({ key: step.key, element, padding: step.padding });
  }
  return out;
}

/** Every data-tour value a tour points at, for the test that checks they exist in the source. */
export function tourTargetNames(tour: TourDef): string[] {
  return tour.steps.flatMap((s) => (Array.isArray(s.target) ? s.target : [s.target]))
    .map((sel) => sel.match(/^\[data-tour="([^"]+)"\]$/)?.[1] || `!${sel}`);
}
